import {
  BookOpen,
  CheckCircle2,
  FlaskConical,
  Gauge,
  GraduationCap,
  Search,
  ShieldCheck,
  X,
} from 'lucide-react';
import { Dialog, PANEL_TITLES } from '@trajectory/ui';
import { useEffect, useMemo, useState } from 'react';
import {
  glossaryEntry,
  OPERATION_GUIDES,
  panelGuideList,
  searchGlossary,
  type GlossaryCategory,
} from '@/lib/education';
import { useSettingsStore, type ExperienceLevel } from '@/stores/settingsStore';
import { useUiStore } from '@/stores/uiStore';

type EducationTab = 'start' | 'glossary' | 'workflows' | 'fidelity';

const TABS: { id: EducationTab; label: string; icon: typeof BookOpen }[] = [
  { id: 'start', label: 'Start here', icon: GraduationCap },
  { id: 'glossary', label: 'Glossary', icon: BookOpen },
  { id: 'workflows', label: 'Workflows', icon: Gauge },
  { id: 'fidelity', label: 'Model fidelity', icon: FlaskConical },
];

const CATEGORY_COLORS: Record<GlossaryCategory, string> = {
  Mission: 'text-info',
  Orbit: 'text-accent',
  Vehicle: 'text-warning',
  Atmosphere: 'text-success',
  Simulation: 'text-info',
  Analysis: 'text-accent',
};

const STUDENT_PATH = [
  {
    title: '1. Define one question',
    body: 'Choose a measurable question such as “How does payload mass affect orbit insertion?” Change one independent variable at a time.',
  },
  {
    title: '2. Build a baseline',
    body: 'Create a mission, clone a reference rocket, select a target orbit, and run the unchanged baseline first.',
  },
  {
    title: '3. Read events with charts',
    body: 'Use the timeline to relate liftoff, Max Q, staging, cutoff, and insertion to telemetry changes.',
  },
  {
    title: '4. Compare reproducibly',
    body: 'Record inputs, mission version, model fidelity, and outputs. Export telemetry for tables, plots, and uncertainty analysis.',
  },
] as const;

function ModeButton({
  level,
  current,
  onSelect,
}: {
  level: ExperienceLevel;
  current: ExperienceLevel;
  onSelect: (level: ExperienceLevel) => void;
}) {
  const guided = level === 'guided';
  return (
    <button
      type="button"
      aria-pressed={current === level}
      onClick={() => onSelect(level)}
      className={`flex-1 rounded-panel border p-3 text-left ${
        current === level
          ? 'border-accent bg-accent/10'
          : 'border-border bg-surface hover:border-border-strong'
      }`}
    >
      <span className="flex items-center gap-2 text-sm font-medium text-text">
        {guided ? (
          <GraduationCap className="h-4 w-4 text-info" />
        ) : (
          <Gauge className="h-4 w-4 text-accent" />
        )}
        {guided ? 'Guided learning' : 'Professional workspace'}
      </span>
      <span className="mt-1 block text-xs leading-relaxed text-muted">
        {guided
          ? 'Shows contextual panel guidance and highlights terms with definitions.'
          : 'Keeps the dense engineering workspace compact; the glossary remains available.'}
      </span>
    </button>
  );
}

export function EducationCenter() {
  const open = useUiStore((state) => state.educationCenterOpen);
  const selectedTermId = useUiStore((state) => state.educationTermId);
  const close = useUiStore((state) => state.closeEducationCenter);
  const experienceLevel = useSettingsStore((state) => state.experienceLevel);
  const setExperienceLevel = useSettingsStore((state) => state.setExperienceLevel);
  const [tab, setTab] = useState<EducationTab>('start');
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open || !selectedTermId) return;
    const entry = glossaryEntry(selectedTermId);
    if (!entry) return;
    setTab('glossary');
    setQuery(entry.abbreviation ?? entry.term);
  }, [open, selectedTermId]);

  const glossaryResults = useMemo(() => searchGlossary(query), [query]);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) close();
      }}
      title="Learning and reference centre"
      className="top-[4%] flex max-h-[92vh] max-w-5xl flex-col"
    >
      <header className="flex items-start gap-3 border-b border-border px-5 py-4">
        <div className="rounded-panel bg-info/10 p-2 text-info">
          <GraduationCap className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold text-text">Learning &amp; reference centre</h2>
          <p className="mt-0.5 text-xs leading-relaxed text-muted">
            Plain-language aerospace definitions for students, plus workflow and model guidance
            for academic and preliminary engineering studies.
          </p>
        </div>
        <button
          type="button"
          onClick={close}
          aria-label="Close learning centre"
          className="rounded-control p-1.5 text-faint hover:bg-surface-sunken hover:text-text"
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      <nav className="flex gap-1 overflow-x-auto border-b border-border px-4 py-2" aria-label="Learning centre sections">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            aria-current={tab === id ? 'page' : undefined}
            className={`flex items-center gap-1.5 whitespace-nowrap rounded-control px-3 py-1.5 text-xs ${
              tab === id
                ? 'bg-accent text-accent-contrast'
                : 'text-muted hover:bg-surface-sunken hover:text-text'
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </nav>

      <main className="min-h-0 flex-1 overflow-y-auto p-5">
        {tab === 'start' && (
          <div className="space-y-5">
            <section>
              <h3 className="text-sm font-semibold text-text">Choose your workspace experience</h3>
              <p className="mt-1 text-xs text-muted">
                This changes explanation density only. Physics, precision, saved data, and
                available tools remain identical.
              </p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <ModeButton
                  level="guided"
                  current={experienceLevel}
                  onSelect={setExperienceLevel}
                />
                <ModeButton
                  level="professional"
                  current={experienceLevel}
                  onSelect={setExperienceLevel}
                />
              </div>
            </section>

            <section>
              <h3 className="text-sm font-semibold text-text">A reproducible student workflow</h3>
              <div className="mt-2 grid gap-2 md:grid-cols-2">
                {STUDENT_PATH.map((step) => (
                  <div
                    key={step.title}
                    className="rounded-panel border border-border bg-surface p-3"
                  >
                    <p className="text-xs font-medium text-text">{step.title}</p>
                    <p className="mt-1 text-xs leading-relaxed text-muted">{step.body}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-panel border border-success/30 bg-success/5 p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-text">
                <ShieldCheck className="h-4 w-4 text-success" />
                Good academic practice
              </div>
              <ul className="mt-2 grid gap-1 text-xs leading-relaxed text-muted sm:grid-cols-2">
                <li>• State the hypothesis and controlled variables.</li>
                <li>• Record units, epoch, vehicle, and launch site.</li>
                <li>• Report the force model and integration steps.</li>
                <li>• Compare against a hand calculation or trusted reference.</li>
                <li>• Include sensitivity or uncertainty where conclusions depend on inputs.</li>
                <li>• Distinguish simulation evidence from operational certification.</li>
              </ul>
            </section>
          </div>
        )}

        {tab === 'glossary' && (
          <div>
            <label className="relative block">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-faint" />
              <span className="sr-only">Search aerospace glossary</span>
              <input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search a term, abbreviation, unit, or concept…"
                className="w-full rounded-control border border-border bg-surface py-2 pl-9 pr-3 text-sm text-text outline-none focus:border-accent"
              />
            </label>
            <p className="mt-2 text-xs text-faint">
              {glossaryResults.length} of {searchGlossary('').length} terms
            </p>
            <div className="mt-3 grid gap-2 md:grid-cols-2">
              {glossaryResults.map((entry) => (
                <article
                  key={entry.id}
                  className="rounded-panel border border-border bg-surface p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-sm font-medium text-text">
                      {entry.term}
                      {entry.abbreviation && (
                        <span className="ml-1.5 text-xs text-faint">
                          ({entry.abbreviation})
                        </span>
                      )}
                    </h3>
                    <span className={`text-[10px] ${CATEGORY_COLORS[entry.category]}`}>
                      {entry.category}
                    </span>
                  </div>
                  <p className="mt-1 text-xs font-medium leading-relaxed text-muted">
                    {entry.definition}
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-faint">{entry.detail}</p>
                  {entry.unit && (
                    <p className="mt-2 text-[10px] uppercase tracking-wide text-info">
                      Typical unit: {entry.unit}
                    </p>
                  )}
                </article>
              ))}
            </div>
            {glossaryResults.length === 0 && (
              <p className="py-12 text-center text-sm text-faint">
                No glossary term matches “{query}”.
              </p>
            )}
          </div>
        )}

        {tab === 'workflows' && (
          <div className="space-y-5">
            <section>
              <h3 className="text-sm font-semibold text-text">What every panel is for</h3>
              <div className="mt-2 grid gap-2 md:grid-cols-2">
                {panelGuideList.map((guide) => (
                  <article
                    key={guide.id}
                    className="rounded-panel border border-border bg-surface p-3"
                  >
                    <h4 className="text-xs font-medium text-text">
                      {PANEL_TITLES[guide.id]}
                    </h4>
                    <p className="mt-1 text-xs leading-relaxed text-muted">{guide.summary}</p>
                    <p className="mt-2 text-xs leading-relaxed text-faint">
                      <strong className="text-text">Start:</strong> {guide.firstAction}
                    </p>
                    <details className="mt-2 text-xs text-faint">
                      <summary className="cursor-pointer text-info">Learner and power-user tips</summary>
                      <p className="mt-1 leading-relaxed">{guide.learnerTip}</p>
                      <p className="mt-1 leading-relaxed">{guide.professionalTip}</p>
                    </details>
                  </article>
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-sm font-semibold text-text">What operations change</h3>
              <div className="mt-2 overflow-hidden rounded-panel border border-border">
                {OPERATION_GUIDES.map((operation) => (
                  <div
                    key={operation.id}
                    className="grid gap-1 border-b border-border bg-surface p-3 last:border-0 sm:grid-cols-[11rem_1fr]"
                  >
                    <div>
                      <p className="text-xs font-medium text-text">{operation.title}</p>
                      <span
                        className={`mt-1 inline-flex rounded-full px-1.5 py-0.5 text-[10px] ${
                          operation.reversible
                            ? 'bg-success/10 text-success'
                            : 'bg-danger/10 text-danger'
                        }`}
                      >
                        {operation.reversible ? 'Recoverable / no deletion' : 'Permanent action'}
                      </span>
                    </div>
                    <div className="text-xs leading-relaxed">
                      <p className="text-muted">{operation.description}</p>
                      <p className="mt-1 text-faint">{operation.effect}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        {tab === 'fidelity' && (
          <div className="space-y-4">
            <section className="rounded-panel border border-info/30 bg-info/5 p-4">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-text">
                <CheckCircle2 className="h-4 w-4 text-info" />
                Appropriate use
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-muted">
                Trajectory GUI is suitable for education, reproducible classroom experiments,
                preliminary mission design, engineering trade studies, and academic simulations
                when assumptions are documented and results are independently checked.
              </p>
            </section>

            <div className="grid gap-3 md:grid-cols-2">
              <section className="rounded-panel border border-border bg-surface p-4">
                <h3 className="text-sm font-medium text-text">Modelled capabilities</h3>
                <ul className="mt-2 space-y-1 text-xs leading-relaxed text-muted">
                  <li>• Deterministic staged ascent and coast propagation</li>
                  <li>• Oblate-Earth frames and rotating atmosphere</li>
                  <li>• J2–J4 harmonics, drag, Sun/Moon gravity, eclipse-aware SRP</li>
                  <li>• Lunar and planetary preliminary targeting and arrival phases</li>
                  <li>• Telemetry, event history, versioned mission inputs, and exports</li>
                </ul>
              </section>
              <section className="rounded-panel border border-warning/30 bg-warning/5 p-4">
                <h3 className="text-sm font-medium text-text">Not operational certification</h3>
                <ul className="mt-2 space-y-1 text-xs leading-relaxed text-muted">
                  <li>• Not a flight-safety, range-safety, or human-rating authority</li>
                  <li>• No navigation covariance, sensor model, or formal uncertainty propagation</li>
                  <li>• Weather profiles are deterministic inputs, not live forecasts</li>
                  <li>• Catalogue, reliability, and cost values require source verification</li>
                  <li>• Do not use alone for real launch commands or hazard decisions</li>
                </ul>
              </section>
            </div>

            <section className="rounded-panel border border-border bg-surface p-4">
              <h3 className="text-sm font-medium text-text">Power-user validation checklist</h3>
              <ol className="mt-2 grid gap-2 text-xs leading-relaxed text-muted sm:grid-cols-2">
                <li>1. Confirm units, epoch, reference frame, and central body.</li>
                <li>2. Review the Analytics fidelity card and termination reason.</li>
                <li>3. Converge the integration time step for sensitive results.</li>
                <li>4. Check mass, thrust, Isp, areas, coefficients, and event timing.</li>
                <li>5. Compare key cases with an analytical or independent numerical result.</li>
                <li>6. Export inputs and telemetry with the reported assumptions.</li>
              </ol>
            </section>
          </div>
        )}
      </main>
    </Dialog>
  );
}
